/**
 * @file components/common/ConfirmationDialog.jsx
 * @description Reusable confirmation dialog component
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import styles from '../../styles/utils/ConfirmationDialog.module.css';

/**
 * Reusable confirmation dialog component
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {function} props.onClose - Function to close the dialog
 * @param {function} props.onConfirm - Function to confirm the action
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message (can include HTML)
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {string} props.error - Error message to display
 * @param {string} props.variant - Button variant: 'danger' | 'primary' (default: 'danger')
 */
const ConfirmationDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isProcessing = false,
    error = null,
    variant = 'danger'
}) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isProcessing) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, isProcessing]);
    
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={!isProcessing ? onClose : undefined}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.confirmTitle}>
                    <AlertTriangle size={24} color="var(--theme-color-error)" />
                    {title}
                </div>
                {error && <div className={styles.errorMessage}>{error}</div>}
                <div 
                    className={styles.confirmMessage}
                    dangerouslySetInnerHTML={{ __html: message }}
                />
                <div className={styles.confirmButtons}>
                    <button 
                        className={`${styles.button} ${styles.cancelButton}`} 
                        onClick={onClose} 
                        disabled={isProcessing}
                    >
                        {cancelText}
                    </button>
                    <button 
                        className={`${styles.button} ${styles[`${variant}Button`]}`}
                        onClick={onConfirm} 
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Processing...' : confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationDialog;